import { ActionIcon, Badge, Button, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { IconMicrophone, IconMicrophoneOff } from '@tabler/icons-react';

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export type RecordingAudioDeviceOption = {
  deviceId: string;
  label: string;
};

export interface RecordingControlsTabProps {
  elapsedSeconds: number;
  isMuted: boolean;
  ending: boolean;
  audioDevices?: RecordingAudioDeviceOption[];
  selectedAudioDeviceId?: string | null;
  onToggleMute: () => void;
  onAudioDeviceChange?: (deviceId: string) => void;
  onEndMeeting: () => void;
}

export function RecordingControlsTab({
  elapsedSeconds,
  isMuted,
  ending,
  audioDevices = [],
  selectedAudioDeviceId = null,
  onToggleMute,
  onAudioDeviceChange,
  onEndMeeting,
}: RecordingControlsTabProps): JSX.Element {
  const micOptions = audioDevices.map((d) => ({
    value: d.deviceId,
    label: d.label,
  }));

  return (
    <Stack gap="lg" p="md">
      <Group gap="sm" align="center" justify="space-between">
        <Group gap="sm" align="center">
          <Badge
            color="red"
            variant="filled"
            size="lg"
            styles={{
              root: {
                animation: 'unify-recording-pulse 1.2s ease-in-out infinite',
              },
            }}
          >
            ● REC
          </Badge>
          <Text fw={600} size="lg">
            {formatElapsed(elapsedSeconds)}
          </Text>
        </Group>
        <Tooltip label={isMuted ? 'Microphone muted — click to unmute' : 'Microphone on — click to mute'}>
          <ActionIcon
            size="xl"
            radius="xl"
            variant={isMuted ? 'filled' : 'light'}
            color={isMuted ? 'red' : 'gray'}
            aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            aria-pressed={isMuted}
            onClick={onToggleMute}
          >
            {isMuted ? <IconMicrophoneOff size={22} /> : <IconMicrophone size={22} />}
          </ActionIcon>
        </Tooltip>
      </Group>

      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Microphone
        </Text>
        <Select
          placeholder={micOptions.length ? 'Select microphone' : 'Waiting for recording window…'}
          data={micOptions}
          value={selectedAudioDeviceId}
          onChange={(value) => {
            if (value) onAudioDeviceChange?.(value);
          }}
          disabled={!micOptions.length || !onAudioDeviceChange}
          searchable={micOptions.length > 5}
          nothingFoundMessage="No microphones found"
        />
        <Text size="xs" c="dimmed">
          Cloud recording uses this input from the Zoom recording window.
        </Text>
      </Stack>

      <Button color="red" loading={ending} onClick={onEndMeeting}>
        End meeting
      </Button>
    </Stack>
  );
}
